const DICT_URL = "https://dictionaryapi.com/api/v3/references/collegiate/json/";
const DICT_KEY = process.env.REACT_APP_DICTIONARY_KEY || "";

async function req(url, options = {}) {
  const res = await fetch(url, { credentials: "same-origin", ...options });
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}

export async function getMe() {
  return req("/api/auth/me/");
}

export async function loginUser(username, password) {
  const res = await fetch("/api/auth/login/", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new Error("Login failed");
  return res.json(); // { isLoggedIn, username, first_name }
}

export async function logoutUser() {
  await fetch("/api/auth/logout/", { method: "POST", credentials: "same-origin" });
}

export async function registerUser(userData) {
  return req("/scrabble/api/v1/user/register/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(userData),
  });
}

export async function getPublicDictionaries() {
  return req("/scrabble/api/v1/public_dictionaries/");
}

export async function getUserDictionaries() {
  return req("/scrabble/api/v1/user_dictionaries/");
}

export async function getDictionaryMeta(id) {
  return req(`/scrabble/api/v1/dictionary/${id}/`);
}

export async function getDictionary(id) {
  return req(`/scrabble/api/v1/dictionary/full/${id}/`);
}

export async function getUserDetails() {
  const res = await fetch("/scrabble/api/v1/user/details/", {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(String(res.status));
  const outer = await res.json();
  return JSON.parse(outer)[0].fields;
}

export async function updateUser(userDetails) {
  return req("/scrabble/api/v1/user/details/", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(userDetails),
  });
}

export async function deleteUser() {
  const res = await fetch("/scrabble/api/v1/user/delete/", {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.text();
}

export async function uploadDictionary(data) {
  const form = new FormData();
  form.append("file", data.file);
  form.append("title", data.title);
  form.append("description", data.description);
  form.append("public", data.isPublic);
  const res = await fetch("/scrabble/api/v1/dictionary/upload/", {
    method: "POST",
    credentials: "same-origin",
    body: form,
  });
  if (!res.ok) throw new Error("Upload failed");
  return res.json();
}

export async function getStacksScore(mode) {
  return req(`/scrabble/api/v1/stacks/score/?mode=${mode}`);
}

export async function submitStacksScore(mode, score) {
  return req("/scrabble/api/v1/stacks/score/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode, score }),
  });
}

export async function getStacksLeaderboard(mode) {
  return req(`/scrabble/api/v1/stacks/leaderboard/?mode=${mode}`);
}

const definitionCache = new Map();

// Merriam-Webster entries wrap run-in text with formatting tokens like
// {it}...{/it} (italics), {b}...{/b} (bold), {sx|word|...} (cross-reference —
// keep the display word, drop the reference target) — none of which mean
// anything outside their own web widget, so flashcards show plain text.
function stripMWMarkup(text) {
  if (!text) return "";
  return text
    .replace(/\{sx\|([^|}]*)\|[^}]*\}/g, "$1")
    .replace(/\{dx[^}]*\}/g, "")
    .replace(/\{a_link\|([^}]*)\}/g, "$1")
    .replace(/\{[^}]*\}/g, "")
    .trim();
}

// Pulls the first verbal-illustration ("vis") example sentence out of an
// entry's defining sequence, if the entry has one. Defensively wrapped —
// this shape is deep and not every part of speech/sense carries a "vis".
function extractExample(entry) {
  try {
    const sense = entry.def[0].sseq[0][0][1];
    const vis = sense.dt.find(([type]) => type === "vis");
    return vis ? stripMWMarkup(vis[1][0].t) : "";
  } catch {
    return "";
  }
}

export async function getWordDefinition(word) {
  if (!DICT_KEY) return null;
  if (definitionCache.has(word)) return definitionCache.get(word);
  try {
    const res = await fetch(`${DICT_URL}${word}?key=${DICT_KEY}`);
    if (!res.ok) {
      // A bad/expired/wrong-product key or rate limit shows up here as a
      // non-JSON error body rather than a thrown fetch error, so it has to
      // be caught explicitly — otherwise it fails the same silent way as
      // no key at all, which makes misconfiguration very hard to spot.
      console.warn(`Word definition lookup failed (${res.status}) — check REACT_APP_DICTIONARY_KEY.`);
      return null;
    }
    const data = await res.json();
    const entry = data[0];
    // A word with no dictionary entry comes back as an array of spelling
    // suggestions (strings) instead of entry objects — `entry.shortdef`
    // is undefined either way, so that's the one check needed.
    const info = (!entry || !entry.shortdef || !entry.shortdef.length)
      ? null
      : {
          partOfSpeech: entry.fl || "",
          pronunciation: entry.hwi?.prs?.[0]?.mw || "",
          definition: entry.shortdef.join("; "),
          example: extractExample(entry),
        };
    definitionCache.set(word, info);
    return info;
  } catch (err) {
    console.warn("Word definition lookup failed:", err);
    return null;
  }
}
